import React, { useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';

import { Button } from '@eika/button';
import { useViewType, ViewType } from '@eika/hooks';
import { IconTypes } from '@eika/icon';
import { ErrorLabel } from '@eika/label';
import { DarkSkeleton } from '@eika/skeleton';

import useTranslation from '../../hooks/useTranslation';

import './SwitchBankOrAgreementButton.scss';

interface Props {
    hasUserAgreements: boolean;
    hasSeveralBanks: boolean;
    handleSwitchBank: () => void;
    isLoading: boolean;
    isError: boolean;
    dataTrackId?: string;
}

const SwitchBankOrAgreementButton: React.FC<Props> = ({
    hasUserAgreements,
    hasSeveralBanks,
    handleSwitchBank,
    isLoading,
    isError,
    dataTrackId,
}) => {
    const translations = useTranslation();
    const viewType = useViewType();
    const buttonText = useCallback(() => {
        if (hasUserAgreements && hasSeveralBanks) return translations.profile.switchBankAndAgreement;
        if (hasSeveralBanks) return translations.profile.switchBank;
        return translations.profile.switchAgreement;
    }, [
        hasSeveralBanks,
        hasUserAgreements,
        translations.profile.switchAgreement,
        translations.profile.switchBank,
        translations.profile.switchBankAndAgreement,
    ]);

    if (isLoading) {
        return (
            <>
                {viewType === ViewType.mobile ? (
                    <Skeleton width={107} height={40} />
                ) : (
                    <DarkSkeleton width={107} height={40} />
                )}
            </>
        );
    }

    if (!hasUserAgreements && !hasSeveralBanks) {
        return <></>;
    }

    return (
        <>
            <Button
                className="user-profile__action-button"
                buttonType="negative"
                onClick={handleSwitchBank}
                disabled={isError}
                data-track-id={dataTrackId || buttonText()}
                data-track-linkname={buttonText()}
            >
                {buttonText()}
            </Button>
            {isError && (
                <div className="switch_error_label">
                    <ErrorLabel
                        className="error_label"
                        icon={IconTypes.UTROPSTEGN_24}
                        text={translations.profile.agreementError}
                    />
                </div>
            )}
        </>
    );
};

export default SwitchBankOrAgreementButton;
